import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppComponent } from '../app.component';
import { bmxurl, getAccess } from 'src/globals';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { BusyService } from '../_services/busy.service';
import { GlobalService } from '../_services/global.service';
import { users_returndata } from 'src/types/user_returndata';
import { booking_returndata } from 'src/types/booking_returndata';
import { emailtemplate_returndata } from 'src/types/emailtemplate_returndata';

@Component({
  selector: 'app-partybooking',
  templateUrl: './partybooking.component.html',
  styleUrls: ['./partybooking.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PartybookingComponent implements OnInit {

  VenueID: string = localStorage.getItem("SmartVenueID")!;
  id: string;
  httpResponse: any;
  Booking: any;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;
  CurrencySymbol: string = "GBP";
  EmailTemplates: any[] = [];
  selectedEmailTemplate: any;
  templateValid: boolean = true;
  today: Date = new Date();
  waiverItems: any[] = [];
  packageWaiverRequired: boolean = false;
  packageQuestionnaireRequired: boolean = false;
  backRoute: string = "/partylist";
  backLabel: string = "Back to Party List";
  fromParam: string = "";

  constructor(
    private http: HttpClient,
    private appComp: AppComponent,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private busyService: BusyService,
    private globalService: GlobalService) {

    let userid = localStorage.getItem("SmartUserID");

    this.http.get(bmxurl + "User/" + userid).subscribe(response => {
      this.httpResponse = (response as users_returndata);
      if (this.httpResponse.succeeded) {
        let user = this.httpResponse.data;
        if (getAccess("HOME", user)) {
          this.AccessAllowed = true;
        }
        this.CompletedLoading = true;
      }
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting User, error:' + error.messages[0]});
    });
  }

  ngOnInit(): void {
    this.globalService.setSmallScreenMenu(false);

    this.id = this.route.snapshot.paramMap.get('id');

    this.fromParam = this.route.snapshot.queryParamMap.get('from') ?? "";

    if (this.fromParam == "newbookings") {
      this.backRoute = "/bookingstoacknowledge";
      this.backLabel = "Return to New Customer Bookings";
    }

    this.busyService.busy();

    this.http.get(bmxurl + "booking/" + this.VenueID + "/" + this.id).subscribe(response => {
      this.Booking = (response as any).data;

      if (this.Booking.currency) {
        this.CurrencySymbol = this.Booking.currency;
      }

      this.http.get(bmxurl + "package/" + this.VenueID + "/" + this.Booking.packageId).subscribe(pkgResponse => {
        const pkg = (pkgResponse as any).data;
        if (pkg) {
          this.packageWaiverRequired = !!pkg.waiverrequired;
          this.packageQuestionnaireRequired = !!pkg.questionnaire?.questionnaireid;
        }
      }, () => {});

      this.getWaivers();
      this.busyService.idle();
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Failed to load booking.'});
      this.busyService.idle();
    });

    this.http.get(bmxurl + "/EmailTemplateList/" + this.VenueID + "/false").subscribe(response => {
      this.httpResponse = response;
      if (this.httpResponse.succeeded) {
        let paymentLink = localStorage.getItem("PaymentLinkEmailTemplate");
        let cancelledPaymentLink = localStorage.getItem("CancelledBookingTemplate");

        this.EmailTemplates = (response as emailtemplate_returndata).data.filter((t: any) => {
          if (paymentLink && t.emailtemplateid === paymentLink) return false;
          if (cancelledPaymentLink && t.emailtemplateid === cancelledPaymentLink) return false;
          return true;
        });
      }
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Error getting Email Templates'});
    });
  }

  editBooking() {
    let queryParams: any = { returnTo: "partybooking" };

    if (this.fromParam) {
      queryParams.from = this.fromParam;
    }

    this.router.navigate(["/editbooking", this.id], { queryParams: queryParams });
  }

  resendConfirmation() {
    this.http.get<any>(bmxurl + "confirmationemail/" + this.VenueID + "/" + this.Booking.bookingId).subscribe(response => {
      if (response.succeeded) {
        this.messageService.add({severity:'success', summary:'Success', detail:'Confirmation email sent.'});
      } else {
        this.messageService.add({severity:'error', summary:'Failure', detail:'Could not send confirmation: ' + response.messages[0]});
      }
    }, error => {
      this.messageService.add({severity:'error', summary:'Failure', detail:'An error occurred sending the confirmation email.'});
    });
  }

  sendEmailToBooking() {
    let betaEmailing = localStorage.getItem("BetaEmailing");
    if (betaEmailing != "true") {
      this.messageService.add({life: 7000, severity:'warn', summary:'Not Allowed', detail:"Our messaging system is in beta mode. You can turn this on in Settings under the Beta section."});
      return;
    }

    if (this.selectedEmailTemplate == null) {
      this.templateValid = false;
      return;
    }

    this.http.get<any>(bmxurl + "sendbookingemail/" + this.VenueID + "/" + this.Booking.bookingId + "/" + this.selectedEmailTemplate.emailtemplateid).subscribe(response => {
      if (!response.succeeded) {
        this.messageService.add({severity:'error', summary:'Failure', detail:'Could not send email: ' + response.messages[0]});
      } else {
        this.messageService.add({severity:'success', summary:'Success', detail:'Email sent.'});
      }
    }, error => {
      this.messageService.add({severity:'error', summary:'Failure', detail:'An error occurred sending the email.'});
    });
  }

  acknowledgeBooking() {
    this.http.get(bmxurl + "acknowledge/" + this.VenueID + "/" + this.Booking.bookingId).subscribe(response => {
      const result = response as any;
      if (result.succeeded) {
        this.Booking.acknowledged = true;
        this.messageService.add({severity:'success', summary:'Acknowledged', detail:'Booking successfully acknowledged'});
      } else {
        this.messageService.add({severity:'error', summary:'Not Acknowledged', detail: 'An error occurred acknowledging this booking'});
      }
    }, error => {
      this.messageService.add({severity:'error', summary:'Not Acknowledged', detail: 'An error occurred acknowledging this Booking'});
    });
  }

  confirmNumbers() {
    this.http.get<any>(bmxurl + "finalisenumbersvenue/" + this.VenueID + "/" + this.Booking.bookingId, {}).subscribe(response => {
      if (response.succeeded) {
        this.messageService.add({severity:'success', summary:'Success', detail:'Final numbers confirmed.'});
      } else {
        this.messageService.add({severity:'error', summary:'Failure', detail:'Could not confirm numbers: ' + response.messages[0]});
      }
    }, error => {
      this.messageService.add({severity:'error', summary:'Failure', detail:'An error occurred confirming numbers.'});
    });
  }

  print() {
    window.print();
  }

  getMultiChoiceAnswers(answers: any[]): string {
    if (!answers || answers.length === 0) return '-';
    return answers.map(a => a.listitemtext).join(', ');
  }

  getBirthdayChild(): any {
    if (!this.Booking?.questionnaire?.bookingquestions) return null;
    for (const q of this.Booking.questionnaire.bookingquestions) {
      if (q.questiontype === 'BIRTHDAY' && q.answers?.birthdayanswers?.length > 0) {
        return q.answers.birthdayanswers[0];
      }
    }
    return null;
  }

  getAllergyDetail(): string {
    if (this.Booking?.allergy) return this.Booking.allergy;
    if (!this.Booking?.questionnaire?.bookingquestions) return '';
    for (const q of this.Booking.questionnaire.bookingquestions) {
      if (q.questiontype === 'ALLERGY' && q.answers?.allergyanswer?.allergypresent) {
        return q.answers.allergyanswer.allergytext || 'Allergy present';
      }
    }
    return '';
  }

  getAnswerText(bq: any): string {
    switch (bq.questiontype) {
      case 'FREETEXT': return bq.answers?.freetextanswer || '';
      case 'NUMBER': case 'AGE': return bq.answers?.numberanswer != null ? String(bq.answers.numberanswer) : '';
      case 'DATE': return bq.answers?.dateanswer ? new Date(bq.answers.dateanswer).toLocaleDateString('en-GB') : '';
      case 'ALLERGY': return bq.answers?.allergyanswer?.allergypresent
        ? (bq.answers.allergyanswer.allergytext || 'Allergy present') : 'No allergy';
      case 'MULTICHOICESINGLE': return bq.answers?.multichoicesingleanswer?.listitemtext || '';
      case 'MULTICHOICEMULTIPLE': return this.getMultiChoiceAnswers(bq.answers?.multichoicemultianswer);
      case 'BIRTHDAY': case 'REGISTER': return (bq.answers?.birthdayanswers || [])
        .map((p: any) => p.name + (p.age ? ' (age ' + p.age + ')' : '')).join(', ');
      default: return '';
    }
  }

  getAdmissionsText(): string {
    if (!this.Booking?.resources?.[0]?.admissions?.length) return '';
    let text = '';
    this.Booking.resources[0].admissions.forEach((adm: any) => {
      if (adm.noofpeople > 0) text += adm.noofpeople + ' x ' + adm.name + ', ';
    });
    return text.length > 2 ? text.slice(0, -2) : '';
  }

  getExtrasText(): string {
    if (!this.Booking?.extras?.length) return '';
    let text = '';
    this.Booking.extras.forEach((e: any) => {
      if (e.qty > 0) text += e.name + ' x ' + e.qty + ', ';
    });
    return text.length > 2 ? text.slice(0, -2) : '';
  }

  getWaivers() {
    this.http.get(bmxurl + "WaiverBookingList/" + this.VenueID + "/" + this.Booking.bookingId).subscribe(response => {
      if ((response as any).succeeded) {
        this.waiverItems = (response as any).data;
      }
    }, error => {});
  }

  calculateChildAge(dateOfBirth: Date): number {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  }

  get packageHasWaivers(): boolean {
    return this.packageWaiverRequired || this.waiverItems.length > 0;
  }

  get packageHasQuestions(): boolean {
    return this.packageQuestionnaireRequired
      || (this.Booking?.questionnaire?.bookingquestions?.length ?? 0) > 0;
  }

  goBack() {
    this.router.navigateByUrl(this.backRoute);
  }
}
